import {clsx} from 'clsx'
import {
  calculateDiffLineCodeCellGutter,
  cellIdFrom,
  getDiffLineMarkersKey,
  getLineBackgroundColor,
  isEmptyDiffLine,
  rowIdFrom,
  trimDiffLineHtml,
} from '../../helpers/line-helpers'
import type {DiffLine, LineKey} from '../../types'
import {useMarkersAtPosition} from '@github-ui/pull-requests/pull-request-store-selectors'
import {InlineMarkers} from '../conversations/InlineMarkers'
import {noop} from '@github-ui/noop'
import {StartConversation} from '../conversations/StartConversation'
import React, {useId, useRef, useState} from 'react'
import {SimpleActionBar} from '../SimpleActionBar'
import {useDialogModeIsEnabled, useDiffLineSelectionInfo, useDiffAnchor} from '../../stores/selectors'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import type {ViewerData} from '../../conversation-types'
import ExpandableHunkHeaderDiffLine from '../ExpandableHunkHeaderDiffLine'
import {getLineHunkData, type HunkData} from '../../helpers/hunk-data-helpers'
import type {DiffLineType} from '@github-ui/diffs/types'
import {LineTextWrapper} from './LineTextWrapper'

const DIMMED_LINE_NUMBER_TYPES: DiffLineType[] = ['CONTEXT', 'INJECTED_CONTEXT']
const EMPTY_CELL_CLASS_NAMES = 'focusable-grid-cell empty-diff-line left-side'
const LINE_NUMBER_CELL_CLASS_NAMES = 'focusable-grid-cell new-diff-line-number left-side'
const CONTENT_CELL_CLASS_NAMES = 'diff-text-cell v-align-top focusable-grid-cell'

export function SimpleSplitDiffRow({
  leftDiffLine,
  rightDiffLine,
  nextDiffLine,
  prevDiffLine,
  filePath,
  handleMouseDownOnNumberCell,
  handleMouseEnterCell,
  handleDiffSideCellSelectionBlocking,
  handleMouseLeaveAddCommentButton,
  handleMouseDownAddCommentButton,
  avatarURL,
  userLogin,
  viewerCanApplySuggestion,
  hunksData,
  viewerCanComment,
  shouldDisplayComments,
}: {
  leftDiffLine: DiffLine | undefined
  rightDiffLine: DiffLine | undefined
  nextDiffLine: DiffLine | undefined
  prevDiffLine: DiffLine | undefined
  hunksData: HunkData[]
  filePath: string
  handleMouseDownOnNumberCell: (e: React.MouseEvent) => void
  handleMouseEnterCell: (e: React.MouseEvent) => void
  handleDiffSideCellSelectionBlocking: (e: React.MouseEvent) => void
  handleMouseLeaveAddCommentButton: (e: React.MouseEvent) => void
  handleMouseDownAddCommentButton: (e: React.MouseEvent) => void
  avatarURL?: string
  userLogin?: string
  viewerCanApplySuggestion?: boolean
  viewerCanComment?: boolean
  shouldDisplayComments?: boolean
}) {
  const isHunkRow = leftDiffLine?.type === 'HUNK'
  const diffAnchor = useDiffAnchor()
  const leftDiffLineSelectionInfo = useDiffLineSelectionInfo(leftDiffLine?.key)
  const rightDiffLineSelectionInfo = useDiffLineSelectionInfo(rightDiffLine?.key)
  const rowId = rowIdFrom(diffAnchor, leftDiffLine ?? 'empty-diff-line', rightDiffLine)
  const leftLineNumberCellId = cellIdFrom(rowId, 0)
  const leftCodeCellId = cellIdFrom(rowId, 1)
  const rightLineNumberCellId = cellIdFrom(rowId, 2)
  const rightCodeCellId = cellIdFrom(rowId, 3)
  const isSelected = (leftDiffLineSelectionInfo.isSelected || rightDiffLineSelectionInfo.isSelected) ?? false

  // TODO: line selection should be different color in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const leftLineNumberBackgroundColor = leftDiffLine ? getLineBackgroundColor(leftDiffLine.type, true, isSelected) : ''
  const rightLineNumberBackgroundColor = rightDiffLine
    ? getLineBackgroundColor(rightDiffLine.type, true, isSelected)
    : ''
  const leftCodeBackgroundColor = leftDiffLine ? getLineBackgroundColor(leftDiffLine.type, false, isSelected) : ''
  const rightCodeBackgroundColor = rightDiffLine ? getLineBackgroundColor(rightDiffLine.type, false, isSelected) : ''
  const inlineDialogHeadingId = `inline-dialog-heading-${useId()}`
  const isLeftInDialogMode = useDialogModeIsEnabled(leftCodeCellId)
  const isRightInDialogMode = useDialogModeIsEnabled(rightCodeCellId)

  const [leftLineHtml, leftLineTypeCharacter] = leftDiffLine
    ? trimDiffLineHtml(leftDiffLine.html, leftDiffLine.type)
    : [undefined, undefined]
  const showLeftLineTypeCharacter = leftLineTypeCharacter && ['+', '-'].includes(leftLineTypeCharacter)

  const [rightLineHtml, rightLineTypeCharacter] = rightDiffLine
    ? trimDiffLineHtml(rightDiffLine.html, rightDiffLine.type)
    : [undefined, undefined]
  const showRightLineTypeCharacter = rightLineTypeCharacter && ['+', '-'].includes(rightLineTypeCharacter)

  const leftLineMarkersKey = leftDiffLine ? getDiffLineMarkersKey(leftDiffLine.left, 'LEFT') : undefined
  const {threads: leftThreads, annotations: leftAnnotations} = useMarkersAtPosition(
    filePath,
    leftLineMarkersKey ?? ('' as LineKey),
  )

  const viewerData = {
    avatarUrl: avatarURL ?? '',
    login: userLogin ?? '',
    viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
    viewerCanComment: viewerCanComment ?? false,
  }
  const rightLineMarkersKey = rightDiffLine ? getDiffLineMarkersKey(rightDiffLine.right, 'RIGHT') : undefined
  const {
    threads: rightThreads,
    annotations: rightAnnotations,
    commentsPreference,
  } = useMarkersAtPosition(filePath, rightLineMarkersKey ?? ('' as LineKey))
  const [isLeftNewConversationDialogOpen, setIsLeftNewConversationDialogOpen] = useState(false)
  const [isLeftActionBarVisible, setIsLeftActionBarVisible] = useState(false)
  const [isLeftContextMenuOpen, setIsLeftContextMenuOpen] = useState(false)
  const [shouldLeftStartNewConversationWithSuggestedChange, setShouldLeftStartNewConversationWithSuggestedChange] =
    useState(false)
  const [isRightNewConversationDialogOpen, setIsRightNewConversationDialogOpen] = useState(false)
  const [isRightActionBarVisible, setIsRightActionBarVisible] = useState(false)
  const [isRightContextMenuOpen, setIsRightContextMenuOpen] = useState(false)
  const [shouldRightStartNewConversationWithSuggestedChange, setShouldRightStartNewConversationWithSuggestedChange] =
    useState(false)
  const [showLeftInlineMarkers, setShowLeftInlineMarkers] = useState(
    leftThreads.length > 0 || leftAnnotations.length > 0 || isLeftNewConversationDialogOpen,
  )
  const [showRightInlineMarkers, setShowRightInlineMarkers] = useState(
    rightThreads.length > 0 || rightAnnotations.length > 0 || isRightNewConversationDialogOpen,
  )
  const hasLeftThreads = leftThreads.length + leftAnnotations.length !== 0
  const hasRightThreads = rightThreads.length + rightAnnotations.length !== 0
  const leftCommentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasLeftThreads && commentsPreference === CommentsPreference.Collapsed,
  })
  const rightCommentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasRightThreads && commentsPreference === CommentsPreference.Collapsed,
  })
  const currentDiffLine = !isEmptyDiffLine(leftDiffLine)
    ? leftDiffLine
    : !isEmptyDiffLine(rightDiffLine)
      ? rightDiffLine
      : undefined
  // TODO: This needs to be reworked in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const inlineMarkersRef = useRef<HTMLDivElement | null>(null)
  const leftCellRef = useRef<HTMLTableCellElement | null>(null)
  const rightCellRef = useRef<HTMLTableCellElement | null>(null)
  if (!currentDiffLine) return null
  const {currentHunk, nextHunk, previousHunk} = getLineHunkData(currentDiffLine, hunksData)

  return (
    <tr className="diff-line-row">
      {isHunkRow ? (
        <ExpandableHunkHeaderDiffLine
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={leftDiffLine}
          isLeftSide
          isSplit
          nextHunk={nextHunk}
          nextLine={nextDiffLine as DiffLine}
          previousHunk={previousHunk}
          prevLine={prevDiffLine as DiffLine}
        />
      ) : (
        <>
          {!leftDiffLine ? (
            <>
              <td role="gridcell" className={EMPTY_CELL_CLASS_NAMES} tabIndex={-1} />
              <td role="gridcell" className={EMPTY_CELL_CLASS_NAMES} tabIndex={-1} />
            </>
          ) : (
            <>
              <td
                className={clsx(
                  LINE_NUMBER_CELL_CLASS_NAMES,
                  DIMMED_LINE_NUMBER_TYPES.includes(leftDiffLine.type) && 'diff-line-number-neutral',
                )}
                data-diff-side="left"
                data-diff-line-key={leftDiffLine.key}
                data-grid-cell-id={leftLineNumberCellId}
                data-line-number={leftDiffLine.left}
                data-selected={isSelected}
                onMouseDown={handleMouseDownOnNumberCell}
                onMouseEnter={handleMouseEnterCell}
                role="gridcell"
                style={{backgroundColor: leftLineNumberBackgroundColor}}
                tabIndex={-1}
              >
                {leftDiffLine.left}
              </td>
              <td
                className={clsx(CONTENT_CELL_CLASS_NAMES, 'left-side-diff-cell')}
                data-grid-cell-id={leftCodeCellId}
                data-diff-line-key={leftDiffLine.key}
                data-diff-side="left"
                data-line-number={leftDiffLine.left}
                data-selected={isSelected}
                style={{backgroundColor: leftCodeBackgroundColor}}
                onMouseDown={handleDiffSideCellSelectionBlocking}
                onMouseEnter={(e: React.MouseEvent) => {
                  setIsLeftActionBarVisible(true)
                  handleMouseEnterCell(e)
                }}
                onMouseLeave={() => setIsLeftActionBarVisible(false)}
                aria-labelledby={isLeftInDialogMode ? inlineDialogHeadingId : undefined}
                role={isLeftInDialogMode ? 'dialog' : 'gridcell'}
                ref={node => {
                  if (!isLeftActionBarVisible) {
                    leftCellRef.current = null
                  } else {
                    leftCellRef.current = node
                  }
                }}
                tabIndex={-1}
              >
                {isLeftInDialogMode && (
                  <h1 id={inlineDialogHeadingId} className="sr-only">
                    Comment view
                  </h1>
                )}
                <LineTextWrapper
                  lineHtml={leftLineHtml ?? ''}
                  lineType={leftDiffLine.type}
                  lineTypeCharacter={showLeftLineTypeCharacter ? leftLineTypeCharacter : undefined}
                />
                {(showLeftInlineMarkers || hasLeftThreads) && shouldDisplayComments && (
                  <InlineMarkers
                    diffLine={leftDiffLine}
                    gridCellId={leftCodeCellId}
                    shouldListenForEscapeKey
                    gutterSizeOffset={leftCommentIndicatorGutterSize}
                    inlineMarkersRef={inlineMarkersRef}
                    cellRef={leftCellRef}
                    fileAnchor={diffAnchor}
                    batchingEnabled
                    // passing a noop because in simplified view we are not dealing with collapsed comments
                    dispatchMarkersStatus={noop}
                    lineMarkersKey={leftLineMarkersKey as LineKey}
                    annotations={leftAnnotations}
                    conversationListThreads={leftThreads}
                    filePath={filePath}
                    onCloseFocusMode={noop}
                    isRowSelected={isSelected}
                    lineType={leftDiffLine.type}
                    onCloseConversationList={() => {
                      setIsLeftNewConversationDialogOpen(false)
                      setShowLeftInlineMarkers(false)
                      setShouldLeftStartNewConversationWithSuggestedChange(false)
                    }}
                    onDeleteLineComment={() => {
                      setIsLeftNewConversationDialogOpen(false)
                      setShowLeftInlineMarkers(false)
                      setShouldLeftStartNewConversationWithSuggestedChange(false)
                    }}
                    viewerData={viewerData as ViewerData}
                  >
                    {isLeftNewConversationDialogOpen && (
                      <div className={clsx(' border rounded-2 color-border-default', 'mt-2 mb-1')}>
                        <StartConversation
                          diffLine={leftDiffLine}
                          diffAnchor={diffAnchor}
                          batchingEnabled
                          filePath={filePath}
                          focusOnMount
                          isLeftSide
                          isInDialogMode={false}
                          isOpen
                          isRowSelected={isSelected}
                          viewerData={viewerData}
                          shouldStartNewConversationWithSuggestedChange={
                            shouldLeftStartNewConversationWithSuggestedChange
                          }
                          onCloseCommentDialog={() => {
                            setIsLeftNewConversationDialogOpen(false)
                            setShowLeftInlineMarkers(false)
                            setShouldLeftStartNewConversationWithSuggestedChange(false)
                          }}
                        />
                      </div>
                    )}
                  </InlineMarkers>
                )}
                {(isLeftActionBarVisible || isLeftContextMenuOpen) && (
                  <SimpleActionBar
                    openStartConversationDialog={(value: boolean) => {
                      setShouldLeftStartNewConversationWithSuggestedChange(value)
                      setIsLeftNewConversationDialogOpen(true)
                      setShowLeftInlineMarkers(true)
                    }}
                    authorAvatarUrl={avatarURL ?? ''}
                    authorLogin={userLogin ?? ''}
                    ref={leftCellRef}
                    copilotChatReference={undefined}
                    currentHunk={currentHunk}
                    diffAnchor={diffAnchor}
                    diffLine={leftDiffLine}
                    threads={leftThreads}
                    annotations={leftAnnotations}
                    hasDraftComment={false}
                    handleMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
                    handleMouseDownAddCommentButton={handleMouseDownAddCommentButton}
                    isLeftSide
                    isSplit
                    contextMenuOpen={isLeftContextMenuOpen}
                    setIsContextMenuOpen={setIsLeftContextMenuOpen}
                    setIsActionBarVisible={setIsLeftActionBarVisible}
                    nextHunk={nextHunk}
                    previousHunk={previousHunk}
                    shouldDisplayComments={shouldDisplayComments}
                    shouldDisplayMinimizedComments={false}
                    shouldDisplayCollapseComments
                    filePath={filePath}
                    toggleViewingMarkers={noop}
                    viewerData={{
                      avatarUrl: avatarURL ?? '',
                      diffViewPreference: 'split',
                      lineSpacingPreference: 'compact',
                      login: userLogin ?? '',
                      shouldDisplayComments: shouldDisplayComments ?? true,
                      shouldDisplayEditOptions: true,
                      tabSizePreference: 8,
                      viewerCanComment: viewerCanComment ?? false,
                      viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
                    }}
                  />
                )}
              </td>
            </>
          )}

          {!rightDiffLine ? (
            <>
              <td role="gridcell" className={EMPTY_CELL_CLASS_NAMES} tabIndex={-1} />
              <td role="gridcell" className={EMPTY_CELL_CLASS_NAMES} tabIndex={-1} />
            </>
          ) : (
            <>
              <td
                className={clsx(
                  LINE_NUMBER_CELL_CLASS_NAMES,
                  DIMMED_LINE_NUMBER_TYPES.includes(rightDiffLine.type) && 'diff-line-number-neutral',
                )}
                data-diff-side="right"
                data-diff-line-key={rightDiffLine.key}
                data-grid-cell-id={rightLineNumberCellId}
                data-line-number={rightDiffLine.right}
                data-selected={isSelected}
                onMouseDown={handleMouseDownOnNumberCell}
                onMouseEnter={handleMouseEnterCell}
                role="gridcell"
                style={{backgroundColor: rightLineNumberBackgroundColor}}
                tabIndex={-1}
              >
                {rightDiffLine.right}
              </td>
              <td
                className={clsx(CONTENT_CELL_CLASS_NAMES, 'right-side-diff-cell')}
                data-grid-cell-id={rightCodeCellId}
                data-diff-line-key={rightDiffLine.key}
                data-diff-side="right"
                data-line-number={rightDiffLine.right}
                data-selected={isSelected}
                style={{backgroundColor: rightCodeBackgroundColor}}
                onMouseDown={handleDiffSideCellSelectionBlocking}
                onMouseEnter={(e: React.MouseEvent) => {
                  setIsRightActionBarVisible(true)
                  handleMouseEnterCell(e)
                }}
                onMouseLeave={() => setIsRightActionBarVisible(false)}
                aria-labelledby={isRightInDialogMode ? inlineDialogHeadingId : undefined}
                role={isRightInDialogMode ? 'dialog' : 'gridcell'}
                ref={node => {
                  if (!isRightActionBarVisible) {
                    rightCellRef.current = null
                  } else {
                    rightCellRef.current = node
                  }
                }}
                tabIndex={-1}
              >
                {isRightInDialogMode && (
                  <h1 id={inlineDialogHeadingId} className="sr-only">
                    Comment view
                  </h1>
                )}
                <LineTextWrapper
                  lineHtml={rightLineHtml ?? ''}
                  lineType={rightDiffLine.type}
                  lineTypeCharacter={showRightLineTypeCharacter ? rightLineTypeCharacter : undefined}
                />
                {(showRightInlineMarkers || hasRightThreads) && shouldDisplayComments && (
                  <InlineMarkers
                    diffLine={rightDiffLine}
                    gridCellId={rightCodeCellId}
                    shouldListenForEscapeKey
                    gutterSizeOffset={rightCommentIndicatorGutterSize}
                    inlineMarkersRef={inlineMarkersRef}
                    cellRef={rightCellRef}
                    fileAnchor={diffAnchor}
                    batchingEnabled
                    // passing a noop because in simplified view we are not dealing with collapsed comments
                    dispatchMarkersStatus={noop}
                    lineMarkersKey={rightLineMarkersKey as LineKey}
                    annotations={rightAnnotations}
                    conversationListThreads={rightThreads}
                    filePath={filePath}
                    viewerData={viewerData as ViewerData}
                    onCloseFocusMode={noop}
                    isRowSelected={isSelected}
                    lineType={rightDiffLine.type}
                    onCloseConversationList={() => {
                      setIsRightNewConversationDialogOpen(false)
                      setShowRightInlineMarkers(false)
                      setShouldRightStartNewConversationWithSuggestedChange(false)
                    }}
                    onDeleteLineComment={() => {
                      setIsRightNewConversationDialogOpen(false)
                      setShowRightInlineMarkers(false)
                      setShouldRightStartNewConversationWithSuggestedChange(false)
                    }}
                  >
                    {isRightNewConversationDialogOpen && (
                      <div className={clsx(' border rounded-2 color-border-default', 'mt-2 mb-1')}>
                        <StartConversation
                          diffLine={rightDiffLine}
                          diffAnchor={diffAnchor}
                          batchingEnabled
                          filePath={filePath}
                          focusOnMount
                          isLeftSide={false}
                          isInDialogMode={false}
                          isOpen
                          isRowSelected={isSelected}
                          shouldStartNewConversationWithSuggestedChange={
                            shouldRightStartNewConversationWithSuggestedChange
                          }
                          viewerData={viewerData}
                          onCloseCommentDialog={() => {
                            setIsRightNewConversationDialogOpen(false)
                            setShowRightInlineMarkers(false)
                            setShouldRightStartNewConversationWithSuggestedChange(false)
                          }}
                        />
                      </div>
                    )}
                  </InlineMarkers>
                )}
                {(isRightActionBarVisible || isRightContextMenuOpen) && (
                  <SimpleActionBar
                    openStartConversationDialog={(value: boolean) => {
                      setShouldRightStartNewConversationWithSuggestedChange(value)
                      setIsRightNewConversationDialogOpen(true)
                      setShowRightInlineMarkers(true)
                    }}
                    authorAvatarUrl={avatarURL ?? ''}
                    authorLogin={userLogin ?? ''}
                    ref={rightCellRef}
                    copilotChatReference={undefined}
                    currentHunk={currentHunk}
                    diffAnchor={diffAnchor}
                    diffLine={rightDiffLine}
                    threads={rightThreads}
                    contextMenuOpen={isRightContextMenuOpen}
                    setIsContextMenuOpen={setIsRightContextMenuOpen}
                    setIsActionBarVisible={setIsRightActionBarVisible}
                    annotations={rightAnnotations}
                    hasDraftComment={false}
                    handleMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
                    handleMouseDownAddCommentButton={handleMouseDownAddCommentButton}
                    isLeftSide={false}
                    isSplit
                    nextHunk={nextHunk}
                    previousHunk={previousHunk}
                    filePath={filePath}
                    shouldDisplayComments={shouldDisplayComments}
                    shouldDisplayMinimizedComments={false}
                    shouldDisplayCollapseComments
                    toggleViewingMarkers={noop}
                    viewerData={{
                      avatarUrl: avatarURL ?? '',
                      diffViewPreference: 'split',
                      lineSpacingPreference: 'compact',
                      login: userLogin ?? '',
                      shouldDisplayComments: shouldDisplayComments ?? true,
                      shouldDisplayEditOptions: true,
                      tabSizePreference: 8,
                      viewerCanComment: viewerCanComment ?? false,
                      viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
                    }}
                  />
                )}
              </td>
            </>
          )}
        </>
      )}
    </tr>
  )
}

try{ SimpleSplitDiffRow.displayName ||= 'SimpleSplitDiffRow' } catch {}